import { createSlice } from '@reduxjs/toolkit';
import { payorsApi } from '@store/rtk/payors';
import { TableData } from '@uikit/Tables/types';
import { beautifyDateTime } from '@src/utils';
import { contractorApi } from '@store/rtk/contractors';
import InfoContent from '@uikit/Info/InfoContent/InfoContent';
import { InfoData } from '@utils/InfoData';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

const { payorRatesColumns, contractorRatesColumns } = TABLE_COLUMNS;

type RatesSliceTypes = {
  payorRatesData: TableData;
  contractorRatesData: TableData;
};

const initialState: RatesSliceTypes = {
  payorRatesData: {
    columns: payorRatesColumns,
    rows: [],
    size: 5,
    page: 0,
  },
  contractorRatesData: {
    columns: contractorRatesColumns,
    rows: [],
    size: 5,
    page: 0,
  },
};

export const ratesSlice = createSlice({
  name: 'rates',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addMatcher(
        payorsApi.endpoints.getPayorRates.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.payorRatesData = {
              ...state.payorRatesData,
              rows: [
                ...payload.content.map((el, idx) => ({
                  ...el,
                  id: el.id ? el.id : idx,
                  baseRate: el.baseRate ? `$${el.baseRate.toFixed(2)}` : null,
                  unitRate: el.baseRate ? `$${el.unitRate.toFixed(2)}` : null,
                  markUpValue: el.markUpValue ? `${el.markUpValue}%` : null,
                  effectiveDate: beautifyDateTime(el?.effectiveDate),
                  expirationDate: beautifyDateTime(el?.expirationDate),
                  bucketStartUnits: el.bucketStartUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketStartUnits)
                    : null,
                  bucketEndUnits: el.bucketEndUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketEndUnits)
                    : null,
                  bucketFreeUnits: el.bucketFreeUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketFreeUnits)
                    : null,
                })),
              ],
              ...payload,
              requestTablePageChange: payorsApi.endpoints.getPayorRates,
              filter: payload.filter || {},
            };
          }
        }
      )
      .addMatcher(
        contractorApi.endpoints.getContractorRates.matchFulfilled,
        (state, { payload }) => {
          if (payload.content && Array.isArray(payload.content)) {
            state.contractorRatesData = {
              ...state.contractorRatesData,
              rows: [
                ...payload.content.map((el, idx) => ({
                  ...el,
                  id: el.id ? el.id : idx,
                  effectiveDate: beautifyDateTime(el?.effectiveDate),
                  expirationDate: beautifyDateTime(el?.expirationDate),
                  bucketStartUnits: el.bucketStartUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketStartUnits)
                    : null,
                  bucketEndUnits: el.bucketEndUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketEndUnits)
                    : null,
                  bucketFreeUnits: el.bucketFreeUnits
                    ? new Intl.NumberFormat('en-US').format(el.bucketFreeUnits)
                    : null,
                  baseRate: el.baseRate ? `$${el.baseRate.toFixed(2)}` : null,
                  unitRate: el.baseRate ? `$${el.unitRate.toFixed(2)}` : null,
                })),
              ],
              ...payload,
              tableInfo: {
                toggle: true,
                desc: <InfoContent contentData={InfoData['ratesTable']} />,
              },
              requestTablePageChange:
                contractorApi.endpoints.getContractorRates,
            };
          }
        }
      );
  },
});

export const selectRates = (state) => state.rates;
export const ratesReducer = ratesSlice.reducer;
